import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CalendarBlank, Clock, MapPin, Gift, Package, ArrowUUpLeft, Check, Cookie, UserCircle,
} from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();

const KIND_META = {
  kid_gift:    { label: 'Return Gift',  icon: Gift,        accent: '#A855F7' },
  nanny_gift:  { label: 'Nanny Gift',   icon: UserCircle,  accent: '#F59E0B' },
  nanny_snack: { label: 'Nanny Snack Box', icon: Cookie,   accent: '#10B981' },
  walkin:      { label: 'Walk-in Spare', icon: Package,    accent: '#64748B' },
};

export default function PackingLabelPage() {
  const { label_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [receivedBy, setReceivedBy] = useState('');
  const [justHanded, setJustHanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/events/labels/${label_id}`);
      setData(r.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Label not found.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [label_id]);

  const handover = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/events/labels/${label_id}/handover`, { received_by: receivedBy });
      setJustHanded(true);
      await load();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  const undo = async () => {
    if (!window.confirm('Undo handover for this bag?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/events/labels/${label_id}/undo`);
      setJustHanded(false);
      await load();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="w-8 h-8 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 px-6 text-center">
      <div className="text-5xl mb-4">🎁</div>
      <h1 className="text-xl font-bold text-neutral-900 mb-2">Label not found</h1>
      <p className="text-sm text-neutral-600 max-w-sm">{error}</p>
      <Link to="/" className="mt-6 text-xs text-neutral-500 underline">Back to home</Link>
    </div>
  );

  const themeColor = data.event?.theme_color || '#FF6B35';
  const kindMeta = KIND_META[data.kind] || KIND_META.kid_gift;
  const KindIcon = kindMeta.icon;
  const isHanded = !!data.handed_over_at;
  const formattedTime = isHanded ? new Date(data.handed_over_at).toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '';

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col" data-testid="packing-label-page">
      {/* Theme stripe */}
      <div className="h-2" style={{ background: themeColor }} />

      <div className="flex-1 max-w-md w-full mx-auto px-5 py-8">
        {/* Event header */}
        <div className="flex items-start gap-3 mb-6">
          <div className="text-3xl">{data.event?.theme_emoji || '🎁'}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: themeColor }}>Packing Label</div>
            <h1 className="text-lg font-bold text-neutral-900 truncate" data-testid="label-event-title">{data.event?.title}</h1>
            <div className="flex flex-wrap gap-3 text-[11px] text-neutral-600 mt-1">
              {data.event?.event_date && <span className="inline-flex items-center gap-1"><CalendarBlank size={11} />{data.event.event_date}</span>}
              {data.event?.event_time && <span className="inline-flex items-center gap-1"><Clock size={11} />{data.event.event_time}</span>}
            </div>
            {data.event?.venue && (
              <div className="inline-flex items-center gap-1 text-[11px] text-neutral-600 mt-1">
                <MapPin size={11} /> {data.event.venue}
              </div>
            )}
          </div>
        </div>

        {/* Big label card */}
        <div className="bg-white rounded-2xl border-2 shadow-sm overflow-hidden mb-4" style={{ borderColor: `${themeColor}30` }}>
          {/* Kind + bucket badges */}
          <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: `${kindMeta.accent}15`, color: kindMeta.accent, border: `1px solid ${kindMeta.accent}40` }}>
              <KindIcon size={11} weight="bold" /> {kindMeta.label}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 tabular-nums" data-testid="label-serial">
              #{String(data.serial || 0).padStart(3, '0')}
            </div>
          </div>

          {/* Recipient */}
          <div className="px-5 py-5">
            <div className="text-[10px] uppercase font-bold tracking-wider text-neutral-500 mb-1">For</div>
            <div className="text-3xl font-bold text-neutral-900 break-words" data-testid="label-recipient-name">{data.recipient_name}</div>
            {data.relation && (
              <div className="text-xs text-neutral-600 mt-1" data-testid="label-relation">{data.relation}</div>
            )}
            {data.host_family && (
              <div className="text-xs text-neutral-500 mt-1" data-testid="label-host">Family · {data.host_family}</div>
            )}
            <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold"
              style={{ background: `${kindMeta.accent}15`, color: kindMeta.accent }}>
              {data.bucket_label}
            </div>
          </div>
        </div>

        {/* Handover panel */}
        {isHanded ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-5 text-center" data-testid="label-handed-state">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500 flex items-center justify-center mb-3">
              <Check size={22} weight="bold" className="text-white" />
            </div>
            <div className="text-base font-bold text-emerald-700">Handed over</div>
            <div className="text-xs text-emerald-700/80 mt-1">at {formattedTime}</div>
            <button onClick={undo} disabled={busy} data-testid="label-undo-btn"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-emerald-200 text-[11px] font-bold text-emerald-700 hover:bg-emerald-100">
              <ArrowUUpLeft size={12} weight="bold" /> Undo
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-neutral-200 px-5 py-5" data-testid="label-handover-panel">
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Confirm handover</div>
            <input type="text" value={receivedBy} onChange={e => setReceivedBy(e.target.value)}
              placeholder="Received by (optional, e.g., Mom, Dad)"
              data-testid="label-received-by"
              className="w-full px-3 py-2.5 rounded-lg bg-neutral-50 border border-neutral-200 text-sm outline-none focus:border-neutral-400" />
            <button onClick={handover} disabled={busy} data-testid="label-handover-btn"
              className="w-full mt-3 py-3 rounded-xl text-white font-bold text-sm inline-flex items-center justify-center gap-2"
              style={{ background: themeColor, opacity: busy ? 0.7 : 1 }}>
              <Check size={14} weight="bold" /> {busy ? 'Saving…' : 'Mark as handed over'}
            </button>
            <p className="text-[10px] text-neutral-500 mt-2 text-center">Scan QR on the bag · single tap to confirm</p>
          </div>
        )}

        {justHanded && (
          <div className="mt-3 text-center text-[11px] text-emerald-600 font-semibold animate-fade-up">
            ✓ Saved. Counts updated in Bags & Gifts dashboard.
          </div>
        )}

        <p className="text-[10px] text-neutral-400 mt-6 text-center">HomeCommand · Bags &amp; Gifts</p>
      </div>
    </div>
  );
}
